import * as d3 from 'd3';
import {first, last, max, min} from 'lodash';
import React from 'react';

import {useIsKeyPressed} from 'src/hooks/useIsKeyPressed';
import {
  ApiForecastTimeSeries,
  ApiObservationTimeSeries,
  ForecastTime,
  TimeSeriesData,
} from 'src/lib/api/series';
import COLORS from 'src/lib/colors';
import {Dims, Margin, TimeSeriesGraphDatum, TimeSeriesRangeGraphDatum} from 'src/lib/commonTypes';
import {
  ForecastTimeSeriesById,
  ObservationTimeSeriesById,
  buildTsById,
  findMatchingTS,
  forecastTSHasColumns,
  isValidDatum,
} from 'src/lib/dataUtils';
import {TimeZoneUTC} from 'src/lib/dates';
import {getGraphDims} from 'src/lib/vizUtils';

import {recordEvent} from './Analytics';
import Axis, {XScale, YScale, defaultNumericTickFormat} from './D3Axis';
import ForecastTimeMarkers from './ForecastTimeMarkers';
import Legend, {LegendConfig} from './Legend';
import TimeSeriesXAxis from './TimeSeriesXAxis';
import {useTimezone} from './TimezoneContext';
import Tooltip, {MouseDateContext} from './Tooltip';
import {VizConfig, VizConfigItem, VizConfigStyle} from './Viz/config';
import {useVizVisibility} from './Viz/VisibilityContext';

// Heights for graph vizualizations. Use the WITH_LEGEND versions for loading
// states and the regular version when setting dims on the graph
export const SOLO_VIZ_HEIGHT = 300;
export const STACKED_VIZ_HEIGHT = 200;
export const LEGEND_HEIGHT = 40;
export const SOLO_VIZ_HEIGHT_WITH_LEGEND = SOLO_VIZ_HEIGHT + LEGEND_HEIGHT;
export const STACKED_VIZ_HEIGHT_WITH_LEGEND = STACKED_VIZ_HEIGHT + LEGEND_HEIGHT;

export type Props = {
  title: string;
  timeZone?: string;
  config: VizConfig;
  observationTimeSeriesList: ApiObservationTimeSeries[];
  forecastTimeSeriesList: ApiForecastTimeSeries[];
  dateRange: [Date, Date];
  setDateRange?: (dateRange: [Date, Date]) => void;
  x: XScale;
  dims: Dims;
  margin: Margin;
  groupId?: string;
  range?: [number | 'auto', number | 'auto'];
  minMaxRangeConstraints?: [number | null, number | null];
  renderSvg?: (
    props: Omit<Props, 'renderSvg' | 'renderHtmlOverlay'> & {y: YScale; graphDims: Dims}
  ) => React.ReactNode;
  renderHtmlOverlay?: (params: {y: YScale; graphDims: Dims; margin: Margin}) => React.ReactNode;
  xTickFormat?: (date: Date, timeZone: string) => string;
  renderLegend?: (legend: {
    configs: LegendConfig[];
    isVisibleById: Record<string, boolean>;
    onChange: (ids: string[], nextValue: boolean) => void;
  }) => React.ReactNode;
};

export default function TimeSeriesViz(props: Props) {
  const {
    title,
    timeZone,
    config,
    observationTimeSeriesList,
    forecastTimeSeriesList,
    dateRange,
    setDateRange,
    x,
    dims,
    margin,
    groupId,
    range = ['auto', 'auto'],
    minMaxRangeConstraints = [null, null],
    renderSvg,
    renderHtmlOverlay,
    xTickFormat,
    renderLegend,
  } = props;

  const {getDisplayTimeZone} = useTimezone();
  const displayTimeZone = getDisplayTimeZone(timeZone ?? TimeZoneUTC);

  /*
  The mouse date context is hoisted out of the Tooltip component so that
  interactions with the forecast time markers can also set the mouse date, even
  though they are children of an element that isn’t a target of pointer events.
  */
  const {mouseDate, setMouseDate} = React.useContext(MouseDateContext);

  const [isHovered, setIsHovered] = React.useState(false);
  const isHideFlagKeyPressed = useIsKeyPressed('KeyF');

  const forecasts = React.useMemo(
    () =>
      config.forecasts.filter(
        (c) =>
          (!groupId || c.groupId === groupId) &&
          !c.isHidden &&
          forecastTSHasColumns(c, findMatchingTS(c, forecastTimeSeriesList))
      ),
    [config.forecasts, groupId, forecastTimeSeriesList]
  );
  const observations = React.useMemo(
    () => config.observations.filter((c) => (!groupId || c.groupId === groupId) && !c.isHidden),
    [config.observations, groupId]
  );

  const legendConfigs = React.useMemo(
    () => [...forecasts.map((c) => c.legend), ...observations.map((c) => c.legend)],
    [forecasts, observations]
  );
  const {isVisibleById, onLegendChange} = useVizVisibility(title + groupId, [
    ...forecasts,
    ...observations,
  ]);

  const [zoomDragRange, setZoomDragRange] = React.useState<{
    range: [number, number | null] | null;
    isDone: boolean;
  }>({range: null, isDone: false});

  React.useEffect(() => {
    if (
      setDateRange &&
      zoomDragRange.range &&
      zoomDragRange.range[0] &&
      zoomDragRange.range[1] &&
      zoomDragRange.isDone
    ) {
      const [x1, x2] = zoomDragRange.range;
      if (Math.abs(x2 - x1) < 50) {
        // check minimum drag range, if too small (pixel-wise) reset
        setZoomDragRange({range: null, isDone: false});
        return;
      }

      const dateA = x.invert(x1);
      const dateB = x.invert(x2);
      if (dateA < dateB) {
        setDateRange([dateA, dateB]);
      } else {
        setDateRange([dateB, dateA]);
      }
      recordEvent('date_range_modified', {method: 'zoom'});
      setZoomDragRange({range: null, isDone: false});
    }
  }, [zoomDragRange, setDateRange, margin.left, x]);

  const [focusedForecastTime, setFocusedForecastTime] = React.useState<ForecastTime | null>(null);

  const graphTransform = `translate(${margin.left}, ${margin.top})`;
  const [graphStartDate, graphEndDate] = dateRange;
  const graphDims = getGraphDims(dims, margin);

  const forecastTsById: ForecastTimeSeriesById = React.useMemo(
    () => buildTsById(config.forecasts, forecastTimeSeriesList),
    [config.forecasts, forecastTimeSeriesList]
  );

  const observationTsById: ObservationTimeSeriesById = React.useMemo(
    () => buildTsById(config.observations, observationTimeSeriesList),
    [config.observations, observationTimeSeriesList]
  );

  const allObsValues = React.useMemo(
    () =>
      observations
        .filter((c) => isVisibleById[c.id])
        .flatMap((c) =>
          c.columns.flatMap((column) =>
            observationTsById[c.id] ? observationTsById[c.id].data[column] : []
          )
        ),
    [observations, observationTsById, isVisibleById]
  );

  const allForecastValues = React.useMemo(
    () =>
      forecasts
        .filter((c) => isVisibleById[c.id])
        .flatMap((c) =>
          c.columns.flatMap((column) =>
            forecastTsById[c.id]
              ? forecastTsById[c.id].forecasts.flatMap((f) => {
                  const startIndex = d3.bisectLeft(f.validTimes, dateRange[0]);
                  const endIndex = d3.bisectRight(f.validTimes, dateRange[1]);
                  // H5 produces a narrower column set than legacy (no q0.05/q0.25/q0.75/q0.95
                  // etc.) so a requested column may be missing from this forecast's data.
                  return (f.data[column] ?? []).slice(startIndex, endIndex);
                })
              : []
          )
        ),
    [forecasts, forecastTsById, isVisibleById, dateRange]
  );

  const [minValue, maxValue] = React.useMemo(() => {
    return [
      min([...allObsValues, ...allForecastValues])!,
      max([...allObsValues, ...allForecastValues])!,
    ];
  }, [allObsValues, allForecastValues]);

  const yPadding = Math.abs(maxValue - minValue) * 0.1;

  let [rangeMin, rangeMax] = range;
  const [rangeMinConstraint, rangeMaxConstraint] = minMaxRangeConstraints;
  if (rangeMin === 'auto') {
    rangeMin = minValue - yPadding;
    if (rangeMinConstraint && rangeMinConstraint < rangeMin) {
      rangeMin = rangeMinConstraint;
    }
  }
  if (rangeMax === 'auto') {
    rangeMax = maxValue + yPadding;
    if (rangeMaxConstraint && rangeMaxConstraint > rangeMax) {
      rangeMax = rangeMaxConstraint;
    }
  }

  const y = d3.scaleLinear().domain([rangeMin, rangeMax]).range([graphDims[1], 0]);

  const clipPathId = `${(groupId || title).replace(/\s+/g, '-')}_viz-bound`;

  return (
    <div className="flex flex-col">
      {/* Only show the groupId header if present. Falling back to showing
       the title isn't needed since it would be a duplicate header*/}
      <div className="text-sm font-semibold">{groupId}</div>
      <div
        className="relative"
        onMouseDown={(e) => {
          if (setDateRange) {
            const mouse = d3.pointer(e);
            setZoomDragRange({
              range: [mouse[0] - margin.left, null],
              isDone: false,
            });
          }
        }}
        onMouseMove={(e) => {
          if (setDateRange && zoomDragRange.range && zoomDragRange.range[0]) {
            const mouse = d3.pointer(e);
            setZoomDragRange({
              range: [zoomDragRange.range[0], mouse[0] - margin.left],
              isDone: false,
            });
          }
        }}
        onMouseUp={(e) => {
          if (setDateRange && zoomDragRange.range && zoomDragRange.range[0]) {
            const mouse = d3.pointer(e);
            setZoomDragRange({
              range: [zoomDragRange.range[0], mouse[0] - margin.left],
              isDone: true,
            });
          }
        }}
        onMouseOver={() => setIsHovered(true)}
        onMouseOut={() => setIsHovered(false)}
      >
        <Tooltip
          graphDims={graphDims}
          x={x}
          xOffset={margin.left}
          yOffset={margin.top}
          timeZone={displayTimeZone}
          snapToX={true}
          forecastTooltipItems={forecasts
            .filter(
              (c) =>
                // visible, has a loaded series, and not hidden when multiple issue times show
                isVisibleById[c.id] &&
                forecastTsById[c.id] &&
                !(forecastTsById[c.id].forecasts.length > 1 && c.hideIfMultipleForecastTimes) &&
                !c.hideTooltip
            )
            .flatMap((c) => {
              return c.columns.map((column) => {
                return {
                  y,
                  column,
                  config: c,
                };
              });
            })}
          observationTooltipItems={observations
            .filter(
              (c) =>
                isVisibleById[c.id] &&
                observationTsById[c.id] &&
                !c.hideIfMultipleForecastTimes &&
                !c.hideTooltip
            )
            .flatMap((c) => {
              return c.columns.map((column) => {
                return {
                  y,
                  column,
                  config: c,
                };
              });
            })}
          observationTimeSeriesById={observationTsById}
          forecastTimeSeriesById={forecastTsById}
          mouseDate={mouseDate}
          setMouseDate={setMouseDate}
          showFlag={isHovered && !isHideFlagKeyPressed}
        />
        <TimeSeriesXAxis
          x={x}
          timeZone={displayTimeZone}
          dateRange={dateRange}
          dims={dims}
          margin={margin}
          tickFormat={xTickFormat}
        />
        <svg
          style={{position: 'absolute'}}
          width={dims[0]}
          height={dims[1]}
          className="pointer-events-none"
        >
          <g transform={graphTransform}>
            {zoomDragRange.range && (
              <rect
                x={min(zoomDragRange.range) as number}
                y={0}
                width={
                  zoomDragRange.range[1]
                    ? Math.abs(zoomDragRange.range[1] - zoomDragRange.range[0])
                    : 10
                }
                height={graphDims[1]}
                fill={COLORS.AXIS_BG}
                opacity={0.5}
              />
            )}
            {forecasts
              .filter(
                (c) => isVisibleById[c.id] && c.showForecastTimeMarkers && forecastTsById[c.id]
              )
              .map((c) => (
                <ForecastTimeMarkers
                  key={c.id}
                  x={x}
                  y={y}
                  graphDims={graphDims}
                  forecastTimeSeriesList={[forecastTsById[c.id]]}
                  getStyle={c.getStyle}
                  column={c.columns[0]}
                  focusedForecastTime={focusedForecastTime}
                  setFocusedForecastTime={setFocusedForecastTime}
                  timeZone={displayTimeZone}
                  startDate={graphStartDate}
                  setMouseDate={setMouseDate}
                />
              ))}
          </g>
        </svg>
        <svg width={dims[0]} height={dims[1]} className="pointer-events-none">
          <defs>
            {/*
              This clip path covers the extent of where timeseries can be
              rendered, and prevents overflowing beyond this bounds.  <g> elements
              that wish to clip to this path use:
                style={{ clipPath: "url(#viz-bound)"}}
              and must not be transformed. If a transform is
              applied, wrap the element in an additional <g>
            */}
            <clipPath id={clipPathId}>
              <rect x={margin.left} y={margin.top} width={graphDims[0]} height={graphDims[1]} />
            </clipPath>
          </defs>

          <g clipPath={`url(#${clipPathId})`}>
            <g transform={graphTransform}>
              {forecasts
                .filter((c) => isVisibleById[c.id])
                .map((c, i) => {
                  const ts = findMatchingTS(c, forecastTimeSeriesList) as ApiForecastTimeSeries;

                  return (
                    <g id={c.displayName} key={i}>
                      {ts &&
                        ts.forecasts.flatMap((f, forecastIndex) => {
                          const isFocused =
                            f.initializationTime.getTime() ===
                            focusedForecastTime?.initializationTime.getTime();

                          if (
                            !isFocused &&
                            ts.forecasts.length > 1 &&
                            c.hideIfMultipleForecastTimes
                          ) {
                            return null;
                          }

                          let style = c.getStyle(forecastIndex, ts.forecasts.length);
                          if (isFocused) {
                            style = {
                              ...style,
                              strokeWidth: ((style['strokeWidth'] || 1) as number) * 2,
                            };
                          }

                          return (
                            <Path
                              key={forecastIndex}
                              style={style}
                              config={c}
                              timesteps={f.validTimes}
                              timeRange={f.validTimeRange}
                              data={f.data}
                              graphStartDate={graphStartDate}
                              graphEndDate={graphEndDate}
                              x={x}
                              y={y}
                            />
                          );
                        })}
                    </g>
                  );
                })}
              {observations
                .filter((c) => isVisibleById[c.id])
                .map((c, i, arr) => {
                  const ts = findMatchingTS(
                    c,
                    observationTimeSeriesList
                  ) as ApiObservationTimeSeries;

                  if (ts) {
                    const {hfStrokeOutlineStyle, ...style} = c.getStyle(i, arr.length);
                    return (
                      <React.Fragment key={i}>
                        {hfStrokeOutlineStyle && (
                          <Path
                            key={`${i}-outline`}
                            style={hfStrokeOutlineStyle}
                            config={c}
                            timesteps={ts.timestamps}
                            timeRange={ts.dataTimeRange}
                            data={ts.data}
                            graphStartDate={graphStartDate}
                            graphEndDate={graphEndDate}
                            x={x}
                            y={y}
                          />
                        )}
                        <Path
                          key={i}
                          style={style}
                          config={c}
                          timesteps={ts.timestamps}
                          timeRange={ts.dataTimeRange}
                          data={ts.data}
                          graphStartDate={graphStartDate}
                          graphEndDate={graphEndDate}
                          x={x}
                          y={y}
                        />
                      </React.Fragment>
                    );
                  }
                  return null;
                })}
            </g>
          </g>
          <g transform={graphTransform}>
            {renderSvg && renderSvg({...props, y, graphDims})}

            {/* Y-Axis */}
            <Axis
              yLabelProps={{
                text: getYAxisLabel(observations, forecasts, observationTsById, forecastTsById),
                graphDims: [graphDims[0], graphDims[1]],
              }}
              axisCreator={d3.axisLeft(y).tickFormat((d) => defaultNumericTickFormat(d.valueOf()))}
            />
          </g>
        </svg>
        {renderHtmlOverlay && renderHtmlOverlay({y, graphDims, margin})}
      </div>
      {renderLegend ? (
        renderLegend({configs: legendConfigs, isVisibleById, onChange: onLegendChange})
      ) : (
        <Legend onChange={onLegendChange} isVisibleById={isVisibleById} configs={legendConfigs} />
      )}
    </div>
  );
}

function Path({
  style,
  config,
  timeRange,
  timesteps,
  data,
  graphStartDate,
  graphEndDate,
  x,
  y,
}: {
  style: VizConfigStyle;
  config: VizConfigItem;
  timesteps: Date[];
  timeRange: [Date, Date];
  data: TimeSeriesData;
  graphStartDate: Date;
  graphEndDate: Date;
  x: XScale;
  y: YScale;
}) {
  const cs = config.columns;
  let ts = timesteps;

  // if the data's range goes beyond the last timestep and we are doing a step
  // curve, we need to manually add one extra point to make the final step display
  if (timeRange[1] > last(timesteps)! && config.curveFactory === d3.curveStepAfter) {
    ts = [...ts, timeRange[1]];
    data = cs.reduce(
      (acc, column) => ({
        ...acc,
        [column]: [...data[column], last(data[column])],
      }),
      {}
    );
  }

  const fillBounds = getFillBounds(cs, data, y.domain()[0]);

  const {stroke, fill, hfStrokeOutlineStyle: _, ...restStyle} = style;
  const key = cs.join('_');

  return (
    <>
      {fill && fillBounds && (
        <path
          key={`${key}-fill`}
          fill={fill}
          {...restStyle}
          d={makeAreaPath(
            ts,
            fillBounds[0],
            fillBounds[1],
            graphStartDate,
            graphEndDate,
            x,
            y,
            config.curveFactory
          )}
        />
      )}
      {stroke &&
        cs.map((c, i) => (
          <path
            key={`${key}-stroke-${i}`}
            fill={'None'}
            stroke={stroke}
            {...restStyle}
            d={makeLinePath(ts, data[c], graphStartDate, graphEndDate, x, y, config.curveFactory)}
          />
        ))}
    </>
  );
}

// The [lower, upper] bound arrays for the area fill under a series' path. A
// series can be missing its first column entirely (e.g. partial API data for
// a band metric), which leaves nothing to derive the synthetic baseline's
// length from, so callers should skip the fill rather than render it.
export function getFillBounds(
  columns: string[],
  data: TimeSeriesData,
  yDomainFloor: number
): [TimeSeriesData[string], TimeSeriesData[string]] | null {
  const d0 = data[columns[0]];
  if (!d0) {
    return null;
  }
  const d1 = data[columns[1]];
  return [columns[1] ? d0 : Array(d0.length).fill(yDomainFloor), columns[1] ? d1 : d0];
}

function makeLinePath(
  timesteps: Date[],
  data: (number | null)[],
  graphStartDate: Date,
  graphEndDate: Date,
  x: XScale,
  y: YScale,
  curveFactory: d3.CurveFactoryLineOnly = d3.curveLinear
) {
  return (
    d3
      .line<TimeSeriesGraphDatum>()
      .curve(curveFactory)
      // D3 calls coordinate accessors only for defined points.
      .defined((d) => isValidDatum(d))
      .x((d) => x(d[0]))
      .y((d) => y(d[1]!))(timesteps.map((time, i) => [time, data[i] ?? null]))!
  );
}

function makeAreaPath(
  timesteps: Date[],
  dataLowerBound: (number | null)[],
  dataUpperBound: (number | null)[],
  graphStartDate: Date,
  graphEndDate: Date,
  x: XScale,
  y: YScale,
  curveFactory: d3.CurveFactory = d3.curveLinear
) {
  return d3
    .area<TimeSeriesRangeGraphDatum>()
    .curve(curveFactory)
    .defined((d) => isValidDatum(d))
    .x((d) => x(d[0]))
    .y0((d) => y(d[1]!))
    .y1((d) => y(d[2]!))(
    timesteps.map((time, i) => [time, dataLowerBound[i] ?? null, dataUpperBound[i] ?? null])
  )!;
}

// naively get the first observation unit or forecast unit, assuming they are
// likely the same if they are put on the same plot (!!)
export function getYAxisLabel(
  observations: VizConfigItem[],
  forecasts: VizConfigItem[],
  observationTsById: ObservationTimeSeriesById,
  forecastTsById: ForecastTimeSeriesById
): string {
  // First available unit among the visible series; a series may be absent
  // (nothing selected / not loaded), so skip rather than deref.
  const firstUnit = (
    items: VizConfigItem[],
    tsById: ObservationTimeSeriesById | ForecastTimeSeriesById
  ) => first(items.flatMap((c) => tsById[c.id]?.variables[c.columns[0]]?.unit ?? [])) ?? '';

  const label = observations.length
    ? firstUnit(observations, observationTsById)
    : forecasts.length
      ? firstUnit(forecasts, forecastTsById)
      : '';

  return label === 'dimensionless' ? 'index' : label;
}
